import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Label + control + error, wired for accessibility.
 *
 * The control is projected rather than owned, so a plain `<input>`, an
 * `<app-text-input>` and the six-box OTP group all get the same label voice,
 * the same error slot and the same spacing without this component knowing
 * anything about them.
 *
 * The a11y contract, and it is a contract because the two halves live in two
 * components: this renders `<label for="{{controlId}}">` and the error
 * paragraph as `id="{{controlId}}-error"`. The projected control is
 * responsible for `id="{{controlId}}"` and, when invalid, for
 * `aria-describedby="{{controlId}}-error"`. `errorIdFor()` below is the one
 * place that string is built.
 */
@Component({
  selector: 'app-form-field',
  host: { class: 'fg' },
  template: `
    <label class="f" [for]="controlId()">
      {{ label() }}
      @if (required()) {
        <span class="ff-req" aria-hidden="true">*</span>
      }
    </label>

    <ng-content />

    @if (error()) {
      <p class="ff-err" [id]="errorId()" role="alert">{{ error() }}</p>
    } @else if (hint() || hintTrailing()) {
      <p class="ff-hint">
        @if (hint()) {
          <span class="ff-hint-main">{{ hint() }}</span>
        }
        @if (hintTrailing()) {
          <span class="ff-hint-trail" aria-live="polite">{{ hintTrailing() }}</span>
        }
      </p>
    }
  `,
  styles: [
    `
      .ff-req {
        color: var(--red);
        margin-left: var(--sp-1);
      }

      .ff-err,
      .ff-hint {
        font-family: var(--mono);
        font-size: 10px;
        line-height: 1.5;
        margin-top: var(--sp-2);
      }

      .ff-err {
        color: var(--fg-sos);
      }

      /*
       * One row, two jobs: the hint explains the field and the trailing slot carries a live
       * character count. They were previously two sibling elements with the counter dragged
       * up by a negative margin, which only lined up while the hint happened to occupy the
       * expected number of lines - widen the dialog or lengthen the sentence and the count
       * landed ON TOP of the hint's last line. A flex row cannot collide.
       */
      .ff-hint {
        color: var(--ink-faint);
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--sp-4);
      }

      .ff-hint-main {
        min-width: 0;
      }

      /* Never wraps and never shrinks - a half-wrapped "500 characters left" reads as broken. */
      .ff-hint-trail {
        flex: 0 0 auto;
        white-space: nowrap;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormField {
  /** The projected control's `id`. Required - a label bound to nothing is worse than no label. */
  readonly controlId = input.required<string>();

  /** Mono micro-caps, matching `label.f` in _components.scss. */
  readonly label = input.required<string>();

  /** Rendered only when there is no error, so the two never stack. */
  readonly hint = input('');

  /**
   * Right-aligned companion to {@link hint}, on the same line - a live character count is the
   * only current caller. It is `aria-live="polite"` because it changes as the user types, and
   * it shares the hint's fate: an error replaces both, so a counter never sits under a
   * validation message arguing with it.
   */
  readonly hintTrailing = input('');

  /** Non-empty switches the field into its error presentation. */
  readonly error = input('');

  readonly required = input(false);

  protected readonly errorId = computed(() => errorIdFor(this.controlId()));
}

/** The single source of the error element id, shared with every projected control. */
export function errorIdFor(controlId: string): string {
  return `${controlId}-error`;
}
